import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  GridFilterInputValueProps,
  GridFilterOperator,
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiContext,
} from "@mui/x-data-grid-premium";
import { renderDate } from "../../../../formatting/dateFormat";
import { SupplierDef } from "../suppliers";

/**
 * One grid column per supplier. The cell rolls up the supplier's member
 * columns (price, qty, in-stock, observed-at, source); the server resolves
 * the field to the price column for sorting and plain filters, and the
 * "matches" operator sends a RollupFilterValue (one condition per member).
 *
 * Every supplier renders the same way: freshness dot, price, ×qty, in-stock
 * glyph, with "—" for a value that is not known (the retail scrapes report no
 * quantity, so their qty is usually unknown). DealsAreUs alone has a second
 * quantity, what their own supplier holds for them to order in; it is shown
 * apart as ×onhand+atsupplier and edited and filtered as its own field.
 */

export interface RollupValue {
  price: number | null;
  qty: number | null;
  /** DealsAreUs only: quantity at their own supplier. */
  supplierQty: number | null;
  inStock: boolean | null;
  observedAt: string | null;
  source: string | null;
}

export function getRollupValue(row: any, supplier: SupplierDef): RollupValue {
  const s = row?.supply ?? {};
  const k = supplier.key;
  const toBool = (v: unknown) =>
    v === 1 || v === true ? true : v === 0 || v === false ? false : null;
  return {
    price: s[`${k}_price`] ?? null,
    qty: s[`${k}_qty`] ?? null,
    supplierQty: supplier.supplierQty ? s[`${k}_supplier_qty`] ?? null : null,
    inStock: toBool(s[`${k}_in_stock`]),
    observedAt: s[`${k}_observed_at`] ?? null,
    source: s[`${k}_source`] ?? null,
  };
}

/** Age bands for the freshness dot. */
export type Freshness = "fresh" | "aging" | "stale" | "never";

export function classifyFreshness(
  observedAt: string | number | Date | null | undefined,
  now: Date = new Date()
): Freshness {
  if (!observedAt) return "never";
  const observed = new Date(observedAt);
  if (Number.isNaN(observed.getTime())) return "never";
  const days = (now.getTime() - observed.getTime()) / 86_400_000;
  if (days <= 2) return "fresh";
  if (days <= 14) return "aging";
  return "stale";
}

const FRESHNESS_COLORS: Record<Freshness, string> = {
  fresh: "#339933",
  aging: "#E6A700",
  stale: "#CC0000",
  never: "#B0B0B0",
};

const FRESHNESS_LABELS: Record<Freshness, string> = {
  fresh: "observed within 2 days",
  aging: "observed within 14 days",
  stale: "older than 14 days",
  never: "never observed",
};

const UNKNOWN = "—";

export const formatPrice = (price: number | null | undefined) =>
  price === null || price === undefined ? UNKNOWN : Number(price).toFixed(2);

export const formatQty = (qty: number | null | undefined) =>
  qty === null || qty === undefined ? UNKNOWN : String(qty);

export const stockGlyph = (inStock: boolean | null | undefined) =>
  inStock === true ? "✓" : inStock === false ? "✕" : "?";

const stockColor = (inStock: boolean | null | undefined) =>
  inStock === true ? "#339933" : inStock === false ? "#CC0000" : "#888888";

interface SummaryValue {
  price: number | null | undefined;
  qty: number | null | undefined;
  supplierQty?: number | null | undefined;
  inStock: boolean | null | undefined;
}

/** `qty`, or `onhand+atsupplier` for a supplier with a second quantity. */
export const formatQtyText = (supplier: SupplierDef, value: SummaryValue) =>
  supplier.supplierQty
    ? `${formatQty(value.qty)}+${formatQty(value.supplierQty)}`
    : formatQty(value.qty);

/** The cell text: `price ×qty glyph`. */
export const formatRollupSummary = (
  supplier: SupplierDef,
  value: SummaryValue
) =>
  `${formatPrice(value.price)} ×${formatQtyText(supplier, value)} ${stockGlyph(
    value.inStock
  )}`;

export function formatRollupTooltip(
  supplier: SupplierDef,
  value: RollupValue
): string {
  const lines = [supplier.name];
  lines.push(`Price: ${formatPrice(value.price)}`);
  if (supplier.supplierQty) {
    lines.push(`Qty on hand: ${formatQty(value.qty)}`);
    lines.push(`Qty at their supplier: ${formatQty(value.supplierQty)}`);
  } else {
    lines.push(`Qty: ${formatQty(value.qty)}`);
  }
  lines.push(
    `In stock: ${
      value.inStock === null ? "unknown" : value.inStock ? "yes" : "no"
    }`
  );
  lines.push(
    `Observed: ${value.observedAt ? renderDate(value.observedAt) : "never"} (${
      FRESHNESS_LABELS[classifyFreshness(value.observedAt)]
    })`
  );
  lines.push(`Source: ${value.source ?? "unknown"}`);
  return lines.join("\n");
}

const hasAnyValue = (v: RollupValue) =>
  v.price !== null ||
  v.qty !== null ||
  v.supplierQty !== null ||
  v.inStock !== null;

export const renderSupplierRollupCell =
  (supplier: SupplierDef) => (params: GridRenderCellParams) => {
    const value = getRollupValue(params.row, supplier);
    if (!hasAnyValue(value)) return null;

    const freshness = classifyFreshness(value.observedAt);

    return (
      <Tooltip
        title={
          <span style={{ whiteSpace: "pre-line" }}>
            {formatRollupTooltip(supplier, value)}
          </span>
        }
        arrow
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            whiteSpace: "nowrap",
          }}
        >
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              flex: "0 0 auto",
              backgroundColor: FRESHNESS_COLORS[freshness],
            }}
            aria-label={FRESHNESS_LABELS[freshness]}
          />
          <span>{formatPrice(value.price)}</span>
          <span style={{ color: "rgba(0,0,0,0.6)" }}>
            ×{formatQtyText(supplier, value)}
          </span>
          <span style={{ color: stockColor(value.inStock), fontWeight: 700 }}>
            {stockGlyph(value.inStock)}
          </span>
        </Box>
      </Tooltip>
    );
  };

// ─── Editing ──────────────────────────────────────────────────────────────────

/**
 * Value committed by the edit cell; the server expands it into the member
 * columns. The in-stock flag is a stored column for every supplier, so it
 * can be set with or without a quantity.
 */
export interface RollupEditValue {
  price?: number | null;
  qty?: number | null;
  /** Only sent for a supplier with a second quantity (DealsAreUs). */
  supplierQty?: number | null;
  inStock?: boolean | null;
}

type InStockChoice = "" | "true" | "false";

const toInStockChoice = (v: boolean | null | undefined): InStockChoice =>
  v === true ? "true" : v === false ? "false" : "";

/** In-stock derived from the quantities the way the feeds and uploads do it. */
function derivedInStock(qty: string, supplierQty: string): InStockChoice {
  if (qty === "" && supplierQty === "") return "";
  return Number(qty || 0) + Number(supplierQty || 0) > 0 ? "true" : "false";
}

export const renderSupplierRollupEditCell =
  (supplier: SupplierDef) => (params: GridRenderEditCellParams) =>
    <SupplierRollupEditCell {...params} supplier={supplier} />;

/**
 * Row edit mode renders the edit component of every editable cell in the
 * row at once, so this cell is a compact summary with an edit affordance; the
 * popover editor opens for the cell the user double-clicked (it has focus) or
 * when the summary is clicked. Apply pushes the value to the grid and saves
 * the row straight away; Cancel on an editor that started the row edit
 * leaves edit mode without changes.
 */
function SupplierRollupEditCell({
  id,
  field,
  row,
  value,
  hasFocus,
  supplier,
}: GridRenderEditCellParams & { supplier: SupplierDef }) {
  const apiRef = useGridApiContext();
  const cellRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [openedByFocus, setOpenedByFocus] = useState(false);

  // The pending edit value, if the popover was already applied once in this
  // row edit session; otherwise the row's current members.
  const current = getRollupValue(row, supplier);
  const pending = (
    value && typeof value === "object" ? value : {}
  ) as RollupEditValue;
  const effective = {
    price: pending.price !== undefined ? pending.price : current.price,
    qty: pending.qty !== undefined ? pending.qty : current.qty,
    supplierQty:
      pending.supplierQty !== undefined
        ? pending.supplierQty
        : current.supplierQty,
    inStock: pending.inStock !== undefined ? pending.inStock : current.inStock,
  };

  // Open once for the cell that started the row edit
  const openedForFocus = React.useRef(false);
  useEffect(() => {
    if (hasFocus && !openedForFocus.current) {
      openedForFocus.current = true;
      setOpenedByFocus(true);
      setOpen(true);
    }
  }, [hasFocus]);

  const summary = formatRollupSummary(supplier, effective);

  return (
    <Box
      ref={cellRef}
      onClick={() => {
        setOpenedByFocus(false);
        setOpen(true);
      }}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        overflow: "hidden",
      }}
      title={`Edit ${supplier.name}`}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
        {summary}
      </span>
      <span style={{ color: "rgba(0,0,0,0.45)", fontSize: "0.8em" }}>✎</span>
      {open && (
        <SupplierRollupEditor
          supplier={supplier}
          anchorEl={cellRef.current}
          initial={effective}
          onCancel={() => {
            setOpen(false);
            if (openedByFocus) {
              apiRef.current.stopRowEditMode({ id, ignoreModifications: true });
            }
          }}
          onApply={async (next) => {
            setOpen(false);
            await apiRef.current.setEditCellValue({ id, field, value: next });
            // Save the row now: the purchaser's workflow is this edit alone
            apiRef.current.stopRowEditMode({ id });
          }}
        />
      )}
    </Box>
  );
}

interface SupplierRollupEditorProps {
  supplier: SupplierDef;
  anchorEl: HTMLElement | null;
  initial: {
    price: number | null;
    qty: number | null;
    supplierQty: number | null;
    inStock: boolean | null;
  };
  onApply: (value: RollupEditValue) => void | Promise<void>;
  onCancel: () => void;
}

/**
 * The popover form. Rules:
 * - typing a quantity sets in-stock from it (qty > 0 → yes, 0 → no, empty → unknown);
 *   DealsAreUs adds its on-hand and at-supplier quantities
 * - choosing "No" or "Unknown" clears the quantities; "Yes" may stand on its
 *   own, for retailers that show no exact quantity
 */
function SupplierRollupEditor({
  supplier,
  anchorEl,
  initial,
  onApply,
  onCancel,
}: SupplierRollupEditorProps) {
  const asText = (n: number | null) =>
    n === null || n === undefined ? "" : String(n);

  const [price, setPrice] = useState(asText(initial.price));
  const [qty, setQty] = useState(asText(initial.qty));
  const [supplierQty, setSupplierQty] = useState(asText(initial.supplierQty));
  const [inStock, setInStock] = useState<InStockChoice>(
    toInStockChoice(initial.inStock)
  );
  const hasSupplierQty = Boolean(supplier.supplierQty);

  const changeQty = (nextQty: string, nextSupplierQty: string) => {
    setQty(nextQty);
    setSupplierQty(nextSupplierQty);
    setInStock(derivedInStock(nextQty, nextSupplierQty));
  };

  const changeInStock = (choice: InStockChoice) => {
    setInStock(choice);
    if (choice === "false" || choice === "") {
      // No stock, or nothing known: a quantity would contradict it
      setQty("");
      setSupplierQty("");
    }
  };

  const apply = () => {
    const toNumber = (t: string) => (t === "" ? null : Number(t));
    const next: RollupEditValue = {
      price: toNumber(price),
      qty: toNumber(qty),
      inStock: inStock === "" ? null : inStock === "true",
    };
    if (hasSupplierQty) next.supplierQty = toNumber(supplierQty);
    onApply(next);
  };

  // The popover is a portal, so its React events still bubble to the grid
  // cell (Enter/Tab would end the row edit, mouseup would move the focus).
  // Keep them inside the editor.
  const onKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      apply();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onCancel}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      onKeyDown={onKeyDown}
      onClick={stop}
      onMouseDown={stop}
      onMouseUp={stop}
      onDoubleClick={stop}
    >
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          minWidth: 240,
        }}
      >
        <Typography variant="subtitle2">{supplier.name}</Typography>
        <TextField
          autoFocus
          size="small"
          label="Price"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
        />
        <TextField
          size="small"
          label={hasSupplierQty ? "Qty on hand" : "Qty"}
          type="number"
          value={qty}
          onChange={(e) => changeQty(e.target.value, supplierQty)}
          slotProps={{ htmlInput: { step: 1, min: 0 } }}
        />
        {hasSupplierQty && (
          <TextField
            size="small"
            label="Qty at their supplier"
            type="number"
            value={supplierQty}
            onChange={(e) => changeQty(qty, e.target.value)}
            slotProps={{ htmlInput: { step: 1, min: 0 } }}
          />
        )}
        <FormControl size="small">
          <InputLabel>In stock</InputLabel>
          <Select
            label="In stock"
            value={inStock}
            onChange={(e) => changeInStock(e.target.value as InStockChoice)}
          >
            <MenuItem value="">Unknown</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={apply}>
            Apply
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Apply saves the row.
        </Typography>
      </Box>
    </Popover>
  );
}

// ─── Filtering ────────────────────────────────────────────────────────────────

/** Mirrors the server's RollupFilterValue. */
export interface RollupFilterValue {
  price?: { operator: string; value?: string | number };
  qty?: { operator: string; value?: string | number };
  /** DealsAreUs only. */
  supplierQty?: { operator: string; value?: string | number };
  inStock?: boolean | "unknown";
  observedAt?: {
    operator: "within" | "olderThan" | "never" | "ever";
    days?: number;
  };
  source?: { operator: string; value?: string };
}

const NUMBER_OPERATORS = [
  { value: ">", label: ">" },
  { value: ">=", label: "≥" },
  { value: "<", label: "<" },
  { value: "<=", label: "≤" },
  { value: "=", label: "=" },
  { value: "!=", label: "≠" },
  { value: "isEmpty", label: "is empty" },
  { value: "isNotEmpty", label: "is not empty" },
];

const VALUELESS = new Set(["isEmpty", "isNotEmpty"]);

/** True when the filter value names at least one usable condition. */
export function isRollupFilterComplete(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const v = value as RollupFilterValue;
  const numberOk = (c?: { operator: string; value?: unknown }) =>
    !!c?.operator &&
    (VALUELESS.has(c.operator) || (c.value !== undefined && c.value !== ""));
  if (numberOk(v.price) || numberOk(v.qty) || numberOk(v.supplierQty))
    return true;
  if (v.inStock === true || v.inStock === false || v.inStock === "unknown")
    return true;
  if (v.observedAt?.operator) {
    if (v.observedAt.operator === "never" || v.observedAt.operator === "ever")
      return true;
    if (
      Number.isFinite(Number(v.observedAt.days)) &&
      v.observedAt.days !== undefined
    )
      return true;
  }
  if (v.source?.operator && v.source.value) return true;
  return false;
}

interface NumberConditionProps {
  label: string;
  condition?: { operator: string; value?: string | number };
  onChange: (c?: { operator: string; value?: string | number }) => void;
}

function NumberCondition({ label, condition, onChange }: NumberConditionProps) {
  const operator = condition?.operator ?? "";
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={operator}
          onChange={(e) => {
            const op = e.target.value;
            onChange(
              op ? { operator: op, value: condition?.value } : undefined
            );
          }}
        >
          <MenuItem value="">any</MenuItem>
          {NUMBER_OPERATORS.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {operator && !VALUELESS.has(operator) && (
        <TextField
          size="small"
          type="number"
          value={condition?.value ?? ""}
          onChange={(e) =>
            onChange({
              operator,
              value: e.target.value === "" ? undefined : e.target.value,
            })
          }
          sx={{ width: 110 }}
        />
      )}
    </Box>
  );
}

export interface SupplierRollupFilterFieldsProps {
  supplier: SupplierDef;
  value: RollupFilterValue | undefined;
  onChange: (value: RollupFilterValue) => void;
}

/** The composite filter editor, shared by the grid filter panel and the advanced builder. */
export function SupplierRollupFilterFields({
  supplier,
  value,
  onChange,
}: SupplierRollupFilterFieldsProps) {
  const v: RollupFilterValue = value ?? {};
  const set = (patch: Partial<RollupFilterValue>) =>
    onChange({ ...v, ...patch });
  const inStock =
    v.inStock === true
      ? "true"
      : v.inStock === false
      ? "false"
      : v.inStock === "unknown"
      ? "unknown"
      : "";
  const observedOp = v.observedAt?.operator ?? "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 0.5,
        minWidth: 260,
      }}
    >
      <NumberCondition
        label="Price"
        condition={v.price}
        onChange={(c) => set({ price: c })}
      />
      <NumberCondition
        label={supplier.supplierQty ? "Qty on hand" : "Qty"}
        condition={v.qty}
        onChange={(c) => set({ qty: c })}
      />
      {supplier.supplierQty && (
        <NumberCondition
          label="Qty at supplier"
          condition={v.supplierQty}
          onChange={(c) => set({ supplierQty: c })}
        />
      )}
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>In stock</InputLabel>
        <Select
          label="In stock"
          value={inStock}
          onChange={(e) => {
            const val = e.target.value;
            set({
              inStock:
                val === "true"
                  ? true
                  : val === "false"
                  ? false
                  : val === "unknown"
                  ? "unknown"
                  : undefined,
            });
          }}
        >
          <MenuItem value="">any</MenuItem>
          <MenuItem value="true">yes</MenuItem>
          <MenuItem value="false">no</MenuItem>
          <MenuItem value="unknown">unknown</MenuItem>
        </Select>
      </FormControl>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Observed</InputLabel>
          <Select
            label="Observed"
            value={observedOp}
            onChange={(e) => {
              const op = e.target
                .value as RollupFilterValue["observedAt"] extends infer T
                ? T extends { operator: infer O }
                  ? O | ""
                  : never
                : never;
              set({
                observedAt: op
                  ? { operator: op, days: v.observedAt?.days ?? 7 }
                  : undefined,
              });
            }}
          >
            <MenuItem value="">any time</MenuItem>
            <MenuItem value="within">within last</MenuItem>
            <MenuItem value="olderThan">older than</MenuItem>
            <MenuItem value="ever">ever observed</MenuItem>
            <MenuItem value="never">never observed</MenuItem>
          </Select>
        </FormControl>
        {(observedOp === "within" || observedOp === "olderThan") && (
          <TextField
            size="small"
            type="number"
            label="days"
            value={v.observedAt?.days ?? ""}
            onChange={(e) =>
              set({
                observedAt: {
                  operator: observedOp,
                  days:
                    e.target.value === "" ? undefined : Number(e.target.value),
                },
              })
            }
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ width: 90 }}
          />
        )}
      </Box>
      <TextField
        size="small"
        label="Source contains"
        value={v.source?.value ?? ""}
        onChange={(e) =>
          set({
            source: e.target.value
              ? { operator: "contains", value: e.target.value }
              : undefined,
          })
        }
        placeholder="ftp, csv, grid:kodi…"
      />
    </Box>
  );
}

function SupplierRollupFilterInput({
  item,
  applyValue,
  supplier,
}: GridFilterInputValueProps & { supplier: SupplierDef }) {
  // Keep an uncontrolled draft so typing isn't swallowed by the grid's debounce
  const [draft, setDraft] = useState<RollupFilterValue>(item.value ?? {});
  useEffect(() => {
    setDraft(item.value ?? {});
  }, [item.value]);

  return (
    <SupplierRollupFilterFields
      supplier={supplier}
      value={draft}
      onChange={(next) => {
        setDraft(next);
        applyValue({ ...item, value: next });
      }}
    />
  );
}

/** Filter operators for a rolled-up supplier column (server-side evaluated). */
export function getSupplierRollupFilterOperators(
  supplier: SupplierDef
): GridFilterOperator[] {
  return [
    {
      label: "matches",
      value: "rollup",
      getApplyFilterFn: () => null,
      InputComponent: (props: GridFilterInputValueProps) => (
        <SupplierRollupFilterInput {...props} supplier={supplier} />
      ),
    },
  ];
}

/** The composite editor bound to one supplier, for the advanced filter builder. */
export function supplierRollupFilterEditor(supplier: SupplierDef) {
  return function BoundSupplierRollupFilterFields({
    value,
    onChange,
  }: {
    value: RollupFilterValue | undefined;
    onChange: (value: RollupFilterValue) => void;
  }) {
    return (
      <SupplierRollupFilterFields
        supplier={supplier}
        value={value}
        onChange={onChange}
      />
    );
  };
}
